import React, { useState, useEffect } from 'react';
import { InputComponent, DropDownComponent } from '../custcomponents';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { AxiosAdminInstance } from '../utils';

interface News {
    id: string;
    date: string;
    newscontent: string;
    validtill?: string;
    createdate: Date;
    status: string;
}

export const ManageNews = () => {
    const [news, setNews] = useState<News[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingNews, setEditingNews] = useState<News | null>(null);
    const [newNews, setNewNews] = useState({
        date: '',
        newscontent: '',
        validtill: '',
        status: 'PUBLISHED'
    });

    useEffect(() => {
        fetchNews();
    }, []);

    useEffect(() => {
        if (editingNews) {
            setNewNews({
                date: editingNews.date,
                newscontent: editingNews.newscontent,
                validtill: editingNews.validtill || '',
                status: editingNews.status
            });
        } else {
            setNewNews({
                date: '',
                newscontent: '',
                validtill: '',
                status: 'PUBLISHED'
            });
        }
    }, [editingNews]);

    const fetchNews = async () => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.get('/api/admin/managenews?admin=true');
            if (response.data.result === 'Success') {
                setNews(response.data.data || []);
            } else {
                toast.error(response.data.msg || 'Failed to fetch news');
            }
        } catch (error) {
            toast.error('Failed to fetch news');
        }
        setLoading(false);
    };

    const handleAddNews = async () => {
        if (!newNews.date.trim()) {
            toast.error('Date is required');
            return;
        }
        if (!newNews.newscontent.trim()) {
            toast.error('News content is required');
            return;
        }
        
        setLoading(true);
        try {
            const data = {
                date: newNews.date,
                newscontent: newNews.newscontent,
                validtill: newNews.validtill || null,
                status: newNews.status
            };

            let response;
            if (editingNews) {
                response = await AxiosAdminInstance.put('/api/admin/managenews', data, {
                    params: { newsId: editingNews.id }
                });
            } else {
                response = await AxiosAdminInstance.post('/api/admin/managenews', data);
            }

            if (response.data.result === 'Success') {
                toast.success(editingNews ? 'News updated successfully' : 'News added successfully');
                setNewNews({
                    date: '',
                    newscontent: '',
                    validtill: '',
                    status: 'PUBLISHED'
                });
                setEditingNews(null);
                fetchNews();
            } else {
                toast.error(response.data.msg || 'Operation failed');
            }
        } catch (error) {
            toast.error(editingNews ? 'Failed to update news' : 'Failed to add news');
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setNewNews({
            date: '',
            newscontent: '',
            validtill: '',
            status: 'PUBLISHED'
        });
        setEditingNews(null);
    };

    const handleDeleteNews = async (newsId: string) => {
        if (!window.confirm('Are you sure you want to delete this news item?')) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.delete('/api/admin/managenews', {
                params: { newsId }
            });
            if (response.data.result === 'Success') {
                toast.success('News deleted successfully');
                fetchNews();
            } else {
                toast.error(response.data.msg || 'Failed to delete news');
            }
        } catch (error) {
            toast.error('Failed to delete news');
        }
        setLoading(false);
    };

    const newsColumns = [
        { 
            name: 'Actions', 
            cell: (row: News) => (
                <div>
                    <button className="btn btn-sm btn-warning me-1" onClick={() => setEditingNews(row)} title="Edit">
                        <i className="fa fa-edit"></i>
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteNews(row.id)} title="Delete">
                        <i className="fa fa-trash"></i>
                    </button>
                </div>
            ) 
        },
        { 
            name: 'Date', 
            selector: (row: News) => row.date, 
            sortable: true,
            width: '150px'
        },
        { 
            name: 'News Content', 
            cell: (row: News) => (
                <div style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.newscontent}>
                    {row.newscontent}
                </div>
            ), 
            sortable: true 
        },
        { 
            name: 'Valid Till', 
            selector: (row: News) => row.validtill || 'No Expiry', 
            sortable: true,
            width: '150px'
        },
        { 
            name: 'Status', 
            selector: (row: News) => row.status, 
            sortable: true,
            width: '120px'
        },
        { 
            name: 'Created Date', 
            selector: (row: News) => new Date(row.createdate).toLocaleDateString(), 
            sortable: true,
            width: '150px'
        }
    ];

    return (
        <>
            <section className="register-sec sec-padding-admin" data-scroll-index="8">
                <div className="container">
                    <div className="sec-title">
                        <span className="icon-inner wow flipInY" data-wow-duration="300ms">
                            <span className="fa-stack">
                                <i className="fa rhex fa-stack-2x"></i>
                                <i className="fa fa-newspaper-o fa-stack-1x"></i>
                            </span>
                        </span>
                        <h3 className="wow fadeInRight" data-wow-duration="500ms">Manage News</h3>
                    </div>
                    <div className="register-sec-details">
                        <form onSubmit={(e) => { e.preventDefault(); handleAddNews(); }}>
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">{editingNews ? 'Edit News' : 'Add New News'}</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="date"
                                            custplaceholder="News Date (YYYY-MM-DD)"
                                            custid="newsDate"
                                            onExternalValue={(val: string) => setNewNews(prev => ({ ...prev, date: val }))}
                                            onExternalBind={newNews.date}
                                            type="date"
                                        />
                                    </div>
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="validtill"
                                            custplaceholder="Valid Till (YYYY-MM-DD) - Optional"
                                            custid="newsValidTill"
                                            onExternalValue={(val: string) => setNewNews(prev => ({ ...prev, validtill: val }))}
                                            onExternalBind={newNews.validtill}
                                            type="date"
                                        />
                                    </div>
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <DropDownComponent
                                            custname="status"
                                            custplaceholder="Select Status"
                                            custid="newsStatus"
                                            onExternalValue={(val: string) => setNewNews(prev => ({ ...prev, status: val }))}
                                            externalstateValue={newNews.status}
                                            names={['DRAFT', 'PUBLISHED']}
                                        />
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <div className="form-group">
                                            <label htmlFor="newsContent">News Content</label>
                                            <textarea
                                                id="newsContent"
                                                className="form-control"
                                                rows={4}
                                                placeholder="Enter news content"
                                                value={newNews.newscontent}
                                                onChange={(e) => setNewNews(prev => ({ ...prev, newscontent: e.target.value }))}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                                  
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        <button className="btn-default" type="submit" disabled={loading}>
                                            {editingNews ? 'Update News' : 'Add News'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                        </button>
                                    </div>
                                             <div className="col-lg-6 col-md-6 col-sm-6">
                                     
                                        {editingNews && (
                                            <button className="btn-default" type="button" onClick={handleCancel}>
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">News Items</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <DataTable
                                            columns={newsColumns}
                                            data={news}
                                            customStyles={{
                                                cells: {
                                                    style: {
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        padding: '8px'
                                                    }
                                                }
                                            }}
                                            pagination
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={[5, 10, 25, 50]}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};